import { Link } from "react-router-dom";
import { Linkedin, Facebook, Twitter, Phone, Mail, MapPin } from "lucide-react";
import { COMPANY, ASSETS } from "@/data/site";

const explore = [
  { label: "About BNM", to: "/about" },
  { label: "Projects & Impact", to: "/projects" },
  { label: "Sustainability", to: "/sustainability" },
  { label: "News & Insights", to: "/news" },
  { label: "Gallery", to: "/gallery" },
  { label: "Press Kit", to: "/press" },
  { label: "Partners", to: "/partners" },
];

const technology = [
  { label: "Technology", to: "/technology" },
  { label: "Carbon Free Generator", to: "/carbon-free-generator" },
  { label: "Solar Energy Calculator", to: "/solar" },
  { label: "Energy Intelligence", to: "/energy" },
  { label: "Products & Solutions", to: "/products" },
  { label: "Innovation & R&D", to: "/innovation" },
];

function FooterCol({ title, items }) {
  return (
    <div>
      <h3 className="font-mono text-[11px] uppercase tracking-[0.22em] text-volt/80 mb-4">{title}</h3>
      <ul className="space-y-2.5">
        {items.map((item) => (
          <li key={item.to + item.label}>
            <Link
              to={item.to}
              data-testid={`footer-link-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className="text-sm text-slate-400 hover:text-volt transition-colors"
            >
              {item.label}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function Footer() {
  return (
    <footer data-testid="site-footer" className="bg-obsidian border-t border-white/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-10 lg:gap-8">
          <div className="lg:col-span-4">
            <Link to="/" className="inline-flex" data-testid="footer-logo" aria-label={`${COMPANY.name} — home`}>
              <img
                src={ASSETS.logo}
                alt={`${COMPANY.name} official logo`}
                className="h-16 w-16 rounded-lg ring-1 ring-white/15"
              />
            </Link>
            <p className="mt-5 text-sm leading-relaxed text-slate-400 max-w-sm">
              BNM Sustainable Energy Limited is a clean-energy technology company from Sierra Leone, developing
              carbon-free generation and storage systems for Africa and the world.
            </p>
            <div className="mt-6 flex items-center gap-3">
              <a href={COMPANY.socials.linkedin} target="_blank" rel="noopener noreferrer" aria-label="BNM on LinkedIn" data-testid="social-linkedin"
                className="inline-flex h-10 w-10 items-center justify-center rounded-md border border-white/15 text-slate-300 hover:text-volt hover:border-volt/50 transition-colors">
                <Linkedin className="h-4 w-4" />
              </a>
              <a href={COMPANY.socials.facebook} target="_blank" rel="noopener noreferrer" aria-label="BNM on Facebook" data-testid="social-facebook"
                className="inline-flex h-10 w-10 items-center justify-center rounded-md border border-white/15 text-slate-300 hover:text-volt hover:border-volt/50 transition-colors">
                <Facebook className="h-4 w-4" />
              </a>
              <a href={COMPANY.socials.x} target="_blank" rel="noopener noreferrer" aria-label="BNM on X" data-testid="social-x"
                className="inline-flex h-10 w-10 items-center justify-center rounded-md border border-white/15 text-slate-300 hover:text-volt hover:border-volt/50 transition-colors">
                <Twitter className="h-4 w-4" />
              </a>
            </div>
          </div>

          <div className="lg:col-span-2">
            <FooterCol title="Explore" items={explore} />
          </div>
          <div className="lg:col-span-2">
            <FooterCol title="Technology" items={technology} />
          </div>

          <div className="lg:col-span-4">
            <h3 className="font-mono text-[11px] uppercase tracking-[0.22em] text-volt/80 mb-4">Contact</h3>
            <ul className="space-y-3 text-sm text-slate-400">
              <li className="flex gap-3">
                <MapPin className="h-4 w-4 mt-0.5 shrink-0 text-volt/70" aria-hidden="true" />
                <span>{COMPANY.addressLines.join(", ")}</span>
              </li>
              <li>
                <a href={COMPANY.phoneHref} data-testid="footer-phone" className="flex gap-3 hover:text-volt transition-colors">
                  <Phone className="h-4 w-4 mt-0.5 shrink-0 text-volt/70" aria-hidden="true" />
                  {COMPANY.phoneDisplay}
                </a>
              </li>
              {COMPANY.emails.map((email) => (
                <li key={email}>
                  <a href={`mailto:${email}`} data-testid={`footer-email-${email}`} className="flex gap-3 hover:text-volt transition-colors break-all">
                    <Mail className="h-4 w-4 mt-0.5 shrink-0 text-volt/70" aria-hidden="true" />
                    {email}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="mt-14 pt-8 border-t border-white/10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <p className="text-xs text-slate-500">
            © {new Date().getFullYear()} BNM Sustainable Energy Limited. All rights reserved.
          </p>
          <div className="flex items-center gap-6">
            <Link to="/privacy" data-testid="footer-link-privacy" className="text-xs text-slate-500 hover:text-volt transition-colors">
              Privacy Policy
            </Link>
            <Link to="/terms" data-testid="footer-link-terms" className="text-xs text-slate-500 hover:text-volt transition-colors">
              Terms of Use
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
